import React, {useState, useEffect} from 'react';
import {
    Users,
    UserCheck,
    Search,
    Loader2,
    AlertTriangle,
    RefreshCw,
    Save,
    Trash2,
    Filter,
    CreditCard,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Download,
    Calendar,
    Clock,
} from 'lucide-react';

const ViewUsersPage = () => {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [userLimits, setUserLimits] = useState({});
    const [limitLoading, setLimitLoading] = useState({});
    const [filterRole, setFilterRole] = useState('');
    const [filterSect, setFilterSect] = useState('');
    const [filterSubscription, setFilterSubscription] = useState('');
    const [isExporting, setIsExporting] = useState(false);

    // Pagination state
    const PAGE_SIZE = 10;
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch('https://api.digitalaalim.com/admin/get-users/', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch users: ${response.statusText}`);
            }

            const data = await response.json();
            // console.log("user data: ", data);
            const processedUsers = data.map(user => ({
                ...user,
                isApproved: user.approval_status === "Approved" || user.approval_status === null,
                approval_status: user.approval_status === null ? "Added by Admin" : user.approval_status
            }));

            setUsers(processedUsers);

            // Initialize user limits with monthly limits
            const limits = {};
            processedUsers.forEach(user => {
                limits[user.email] = user.usage_limits?.monthly || 50;
            });
            setUserLimits(limits);
        } catch (error) {
            console.error('Error fetching users:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const exportUsersToCSV = async () => {
        setIsExporting(true);

        try {
            const accessToken = localStorage.getItem('accessToken');

            // Build URL with optional sect filter
            let url = 'https://api.digitalaalim.com/admin/export-users-to-csv/';
            if (filterSect) {
                url += `?sect=${encodeURIComponent(filterSect)}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to export users: ${response.statusText}`);
            }

            // Get the blob from response
            const blob = await response.blob();

            // Create download link
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;

            // Generate filename with timestamp and optional sect filter
            const timestamp = new Date().toISOString().split('T')[0];
            const sectSuffix = filterSect ? `_${filterSect}` : '';
            link.download = `users_export${sectSuffix}_${timestamp}.csv`;

            // Trigger download
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);

            alert('Users exported successfully!');
        } catch (error) {
            console.error('Error exporting users:', error);
            alert(`Failed to export users: ${error.message}`);
        } finally {
            setIsExporting(false);
        }
    };

    const approveUser = async (email) => {
        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch(`https://api.digitalaalim.com/admin/approve-user/?email=${email}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to approve user: ${response.statusText}`);
            }

            setUsers(prevUsers =>
                prevUsers.map(user =>
                    user.email === email
                        ? {...user, isApproved: true, approval_status: "Approved"}
                        : user
                )
            );

            alert("User approved successfully");
        } catch (error) {
            console.error('Error approving user:', error);
            alert(`Failed to approve user: ${error.message}`);
        }
    };

    const deleteUser = async (email) => {
        const confirmed = window.confirm('Are you sure you want to delete this user?');
        if (!confirmed) return;

        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch('https://api.digitalaalim.com/admin/delete-user/', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({email})
            });

            if (!response.ok) {
                throw new Error(`Failed to delete user: ${response.statusText}`);
            }

            setUsers(prevUsers => prevUsers.filter(user => user.email !== email));

            alert("User deleted successfully");
        } catch (error) {
            console.error('Error deleting user:', error);
            alert(`Failed to delete user: ${error.message}`);
        }
    };

    const handleLimitChange = (email, value) => {
        setUserLimits(prev => ({
            ...prev,
            [email]: parseInt(value, 10)
        }));
    };

    const handleSetQueryLimit = async (email) => {
        setLimitLoading(prev => ({...prev, [email]: true}));

        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch('https://api.digitalaalim.com/admin/set-query-limit/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    email,
                    new_limit: userLimits[email],
                }),
            });

            if (!response.ok) {
                throw new Error(`Failed to update query limit: ${response.statusText}`);
            }

            // Refresh users to get updated data
            await fetchUsers();

            alert("Query limit updated successfully");
        } catch (error) {
            console.error('Error setting query limit:', error);
            alert(`Failed to update query limit: ${error.message}`);
        } finally {
            setLimitLoading(prev => ({...prev, [email]: false}));
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getUsagePercentage = (used, limit) => {
        if (!limit || limit === 0) return 0;
        return Math.min(100, (used / limit) * 100);
    };

    const getUsageColor = (percentage) => {
        if (percentage > 80) return 'bg-red-500';
        if (percentage > 50) return 'bg-yellow-500';
        return 'bg-green-500';
    };

    // Filter and search functionality
    const filteredUsers = users.filter(user => {
        const matchesSearch =
            user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.role.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesRole = filterRole ? user.role === filterRole : true;
        const matchesSect = filterSect ? user.sect === filterSect : true;
        const matchesSubscription = filterSubscription ? user.plan_name === filterSubscription : true;

        return matchesSearch && matchesRole && matchesSect && matchesSubscription;
    });

    // Derived pagination values
    const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * PAGE_SIZE;
    const endIndex = Math.min(startIndex + PAGE_SIZE, filteredUsers.length);
    const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

    // Reset to page 1 whenever filters/search change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterRole, filterSect, filterSubscription]);

    // Clamp current page if filtered list shrinks
    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalPages]);

    // Pagination helpers
    const goToPage = (p) => setCurrentPage(Math.min(Math.max(1, p), totalPages));
    const goFirst = () => goToPage(1);
    const goPrev = () => goToPage(currentPage - 1);
    const goNext = () => goToPage(currentPage + 1);
    const goLast = () => goToPage(totalPages);

    const getPageNumbers = () => {
        const pages = [];
        const siblingCount = 1;

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
            return pages;
        }

        const left = Math.max(2, currentPage - siblingCount);
        const right = Math.min(totalPages - 1, currentPage + siblingCount);

        pages.push(1);
        if (left > 2) pages.push('…');
        for (let i = left; i <= right; i++) pages.push(i);
        if (right < totalPages - 1) pages.push('…');
        pages.push(totalPages);
        return pages;
    };

    // Get unique values for filters
    const roles = [...new Set(users.map(user => user.role))];
    const sects = [...new Set(users.map(user => user.sect))];
    const subscriptions = [...new Set(users.map(user => user.plan_name))];

    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <Users className="text-blue-600" size={32}/>
                    Digital Aalim Users
                </h1>
                <p className="mt-2 text-gray-600">
                    Manage users, approve accounts, and set query limits
                </p>
            </div>

            {/* Filters and search */}
            <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                    <div className="relative w-full md:w-80">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search size={16} className="text-gray-400"/>
                        </div>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by name, email or role..."
                            className="pl-10 w-full border border-gray-300 rounded-md py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="flex flex-wrap gap-2 w-full lg:w-auto">
                        <div className="inline-flex items-center">
                            <Filter size={14} className="text-gray-500 mr-1"/>
                            <select
                                value={filterRole}
                                onChange={(e) => setFilterRole(e.target.value)}
                                className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">All Roles</option>
                                {roles.map(role => (
                                    <option key={role} value={role}>{role}</option>
                                ))}
                            </select>
                        </div>

                        <div className="inline-flex items-center">
                            <Filter size={14} className="text-gray-500 mr-1"/>
                            <select
                                value={filterSect}
                                onChange={(e) => setFilterSect(e.target.value)}
                                className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">All Sects</option>
                                {sects.map(sect => (
                                    <option key={sect} value={sect}>{sect}</option>
                                ))}
                            </select>
                        </div>

                        <div className="inline-flex items-center">
                            <Filter size={14} className="text-gray-500 mr-1"/>
                            <select
                                value={filterSubscription}
                                onChange={(e) => setFilterSubscription(e.target.value)}
                                className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">All Subscriptions</option>
                                {subscriptions.map(sub => (
                                    <option key={sub} value={sub}>{sub}</option>
                                ))}
                            </select>
                        </div>

                        <button
                            onClick={exportUsersToCSV}
                            disabled={isExporting || users.length === 0}
                            className="inline-flex items-center px-3 py-1 text-sm font-medium text-green-700 bg-green-50 rounded-md hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title={filterSect ? `Export users filtered by sect: ${filterSect}` : 'Export all users'}
                        >
                            {isExporting ? (
                                <>
                                    <Loader2 size={14} className="mr-1 animate-spin"/>
                                    Exporting...
                                </>
                            ) : (
                                <>
                                    <Download size={14} className="mr-1"/>
                                    Export CSV
                                </>
                            )}
                        </button>

                        <button
                            onClick={fetchUsers}
                            className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                        >
                            <RefreshCw size={14} className="mr-1"/>
                            Refresh
                        </button>
                    </div>
                </div>

                {filterSect && (
                    <div className="mt-3 text-xs text-gray-600 bg-blue-50 px-3 py-2 rounded-md">
                        <strong>Note:</strong> Export will include only users from the selected sect: <span
                        className="font-medium">{filterSect}</span>
                    </div>
                )}
            </div>

            {/* Users Table */}
            {isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <Loader2 size={40} className="animate-spin text-blue-600"/>
                </div>
            ) : error ? (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                    <div className="flex items-center">
                        <AlertTriangle size={24} className="text-red-500 mr-3"/>
                        <div>
                            <h3 className="text-red-800 font-medium">Error loading users</h3>
                            <p className="text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                            <tr>
                                <th scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    User
                                </th>
                                <th scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Role & Sect
                                </th>
                                <th scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Created At
                                </th>
                                <th scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Last Query Date
                                </th>
                                <th scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Subscription
                                </th>
                                <th scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Usage Stats
                                </th>
                                <th scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Monthly Limit
                                </th>
                                <th scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {filteredUsers.length > 0 ? (
                                paginatedUsers.map((user) => {
                                    const dailyUsed = user.usage_used?.daily || 0;
                                    const dailyLimit = user.usage_limits?.daily || 0;
                                    const monthlyUsed = user.usage_used?.monthly || 0;
                                    const monthlyLimit = user.usage_limits?.monthly || 0;
                                    const dailyRemaining = user.usage_remaining?.daily || 0;
                                    const monthlyRemaining = user.usage_remaining?.monthly || 0;
                                    const dailyReset = user.usage_resets?.daily_on;
                                    const monthlyKey = user.usage_resets?.monthly_key;

                                    const dailyPercentage = getUsagePercentage(dailyUsed, dailyLimit);
                                    const monthlyPercentage = getUsagePercentage(monthlyUsed, monthlyLimit);

                                    return (
                                        <tr key={user.email} className="hover:bg-gray-50">
                                            <td className="px-3 py-4">
                                                <div className="flex items-start">
                                                    <div
                                                        className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                                    <span className="text-blue-700 font-medium">
                                                      {user.name.charAt(0).toUpperCase()}
                                                    </span>
                                                    </div>
                                                    <div className="ml-4">
                                                        <div
                                                            className="text-sm font-medium text-gray-900">{user.name}</div>
                                                        <div className="text-sm text-gray-500">{user.email}</div>
                                                        <div className="text-xs text-gray-400">
                                                            {user.auth_method || 'Email/Password'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col">
                                                <span
                                                    className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                                    {user.role}
                                                </span>
                                                    <span className="mt-1 text-sm text-gray-600">
                                                    {user.sect}
                                                </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col">
                                                <span
                                                    className="px-2 inline-flex text-sm text-gray-600 leading-5 ">
                                                    {formatDate(user.created_at)}
                                                </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col">
                                                <span
                                                    className="px-2 inline-flex text-sm text-gray-600 leading-5 ">
                                                    {formatDate(user.last_query_date)}
                                                </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <CreditCard size={16} className="text-gray-400 mr-2"/>
                                                    <span className={`text-sm ${
                                                        user.plan_name === 'PREMIUM'
                                                            ? 'text-purple-600 font-medium'
                                                            : user.plan_name === 'STANDARD'
                                                                ? 'text-blue-600 font-medium'
                                                                : 'text-gray-600'
                                                    }`}>
                                                    {user.plan_name || 'FREE'}
                                                </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="space-y-3">
                                                    {/* Daily Usage */}
                                                    <div>
                                                        <div className="flex items-center justify-between mb-1">
                                                            <div className="flex items-center text-xs text-gray-600">
                                                                <Clock size={12} className="mr-1"/>
                                                                <span className="font-medium">Daily</span>
                                                            </div>
                                                            <span className="text-xs text-gray-500">
                                                            {dailyUsed} / {dailyLimit}
                                                        </span>
                                                        </div>
                                                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                            <div
                                                                className={`h-1.5 rounded-full ${getUsageColor(dailyPercentage)}`}
                                                                style={{width: `${dailyPercentage}%`}}
                                                            />
                                                        </div>
                                                        <div className="flex items-center justify-between mt-0.5">
                                                            <span className="text-xs text-green-600">
                                                                {dailyRemaining} left
                                                            </span>
                                                            {dailyReset && (
                                                                <span className="text-xs text-gray-400">
                                                                    Resets: {formatDate(dailyReset)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Monthly Usage */}
                                                    <div>
                                                        <div className="flex items-center justify-between mb-1">
                                                            <div className="flex items-center text-xs text-gray-600">
                                                                <Calendar size={12} className="mr-1"/>
                                                                <span className="font-medium">Monthly</span>
                                                            </div>
                                                            <span className="text-xs text-gray-500">
                                                            {monthlyUsed} / {monthlyLimit}
                                                        </span>
                                                        </div>
                                                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                            <div
                                                                className={`h-1.5 rounded-full ${getUsageColor(monthlyPercentage)}`}
                                                                style={{width: `${monthlyPercentage}%`}}
                                                            />
                                                        </div>
                                                        <div className="flex items-center justify-between mt-0.5">
                                                            <span className="text-xs text-green-600">
                                                                {monthlyRemaining} left
                                                            </span>
                                                            {monthlyKey && (
                                                                <span className="text-xs text-gray-400">
                                                                    Period: {monthlyKey}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={userLimits[user.email] || ''}
                                                        onChange={(e) => handleLimitChange(user.email, e.target.value)}
                                                        className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                    <button
                                                        onClick={() => handleSetQueryLimit(user.email)}
                                                        disabled={limitLoading[user.email] || userLimits[user.email] === monthlyLimit}
                                                        className="ml-2 inline-flex items-center p-1 text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        title="Update monthly limit"
                                                    >
                                                        {limitLoading[user.email] ? (
                                                            <Loader2 size={16} className="animate-spin"/>
                                                        ) : (
                                                            <Save size={16}/>
                                                        )}
                                                    </button>
                                                </div>
                                                <div className="mt-1 text-xs text-gray-500">
                                                    Current: {monthlyLimit}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex items-center space-x-2">
                                                    {!user.isApproved && (
                                                        <button
                                                            onClick={() => approveUser(user.email)}
                                                            className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200 transition-colors"
                                                        >
                                                            <UserCheck size={14} className="mr-1"/>
                                                            Approve
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => deleteUser(user.email)}
                                                        className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200 transition-colors"
                                                    >
                                                        <Trash2 size={14} className="mr-1"/>
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={6} className="px-6 py-16 text-center text-gray-500">
                                        {searchTerm || filterRole || filterSect || filterSubscription ? (
                                            <div className="flex flex-col items-center">
                                                <Search size={40} className="text-gray-400 mb-2"/>
                                                <p>No users found matching your filters</p>
                                                <button
                                                    onClick={() => {
                                                        setSearchTerm('');
                                                        setFilterRole('');
                                                        setFilterSect('');
                                                        setFilterSubscription('');
                                                    }}
                                                    className="mt-2 text-blue-600 hover:underline"
                                                >
                                                    Clear all filters
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center">
                                                <Users size={40} className="text-gray-400 mb-2"/>
                                                <p>No users available</p>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination controls */}
                    {filteredUsers.length > 0 && (
                        <div
                            className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t">
                            <div className="text-sm text-gray-700">
                                Showing <span
                                className="font-medium">{filteredUsers.length === 0 ? 0 : startIndex + 1}</span>
                                –
                                <span className="font-medium">{endIndex}</span> of{' '}
                                <span className="font-medium">{filteredUsers.length}</span> users
                                <span className="ml-2 text-gray-400">• Page {safePage} of {totalPages}</span>
                            </div>

                            <div className="flex items-center gap-1">
                                <button
                                    onClick={goFirst}
                                    disabled={safePage === 1}
                                    className="inline-flex items-center rounded-md border px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                    aria-label="First page"
                                >
                                    <ChevronsLeft size={16}/>
                                </button>
                                <button
                                    onClick={goPrev}
                                    disabled={safePage === 1}
                                    className="inline-flex items-center rounded-md border px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                    aria-label="Previous page"
                                >
                                    <ChevronLeft size={16}/>
                                </button>

                                {getPageNumbers().map((p, idx) =>
                                    p === '…' ? (
                                        <span
                                            key={`ellipsis-${idx}`}
                                            className="px-2 py-1 text-sm text-gray-400 select-none"
                                        >
                                          …
                                        </span>
                                    ) : (
                                        <button
                                            key={p}
                                            onClick={() => goToPage(p)}
                                            className={`min-w-8 inline-flex items-center justify-center rounded-md border px-3 py-1 text-sm ${
                                                p === safePage
                                                    ? 'bg-blue-600 text-white border-blue-600'
                                                    : 'text-gray-700 hover:bg-gray-50'
                                            }`}
                                            aria-current={p === safePage ? 'page' : undefined}
                                        >
                                            {p}
                                        </button>
                                    )
                                )}

                                <button
                                    onClick={goNext}
                                    disabled={safePage === totalPages}
                                    className="inline-flex items-center rounded-md border px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                    aria-label="Next page"
                                >
                                    <ChevronRight size={16}/>
                                </button>
                                <button
                                    onClick={goLast}
                                    disabled={safePage === totalPages}
                                    className="inline-flex items-center rounded-md border px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                    aria-label="Last page"
                                >
                                    <ChevronsRight size={16}/>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Summary footer */}
                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                        <div className="flex flex-wrap justify-between items-center gap-4">
                            <div className="text-sm text-gray-700">
                                Total users: <span className="font-medium">{users.length}</span>
                            </div>

                            <div className="flex items-center gap-6">
                                <div className="flex items-center gap-2">
                                    <div className="h-3 w-3 rounded-full bg-green-500"></div>
                                    <span className="text-xs text-gray-600">Low Usage (&lt;50%)</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                                    <span className="text-xs text-gray-600">Medium (50-80%)</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="h-3 w-3 rounded-full bg-red-500"></div>
                                    <span className="text-xs text-gray-600">High Usage (&gt;80%)</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewUsersPage;